import { Box, Grid } from "@mui/material";
import { ReactNode } from "react";
import WaveWhite1 from "../../assets/images/Wave-White-1.png";
import OrganizationLogo from "../organization/components/OrganizationLogo";

export type LandingPageWrapperProps = {
  children: ReactNode;
};

/**
 * This is meant to be a wrapper for a single component.
 * It contains the organization logo and its' theme colors.
 * Automatically formats it in a way that is consistent with
 * the runway.works main page.
 */
const LandingPageWrapper = ({ children }: LandingPageWrapperProps) => (
  <Box
    sx={{
      position: "relative",
      minHeight: "100vh",
      overflow: "hidden",
      backgroundImage: `url(${WaveWhite1})`,
      backgroundRepeat: "no-repeat",
      backgroundSize: "cover",
      backgroundPosition: "center",
    }}
  >
    {/* Decorative squares ... */}
    <Box
      sx={(t) => ({
        display: {
          xs: "none",
          sm: "none",
          md: "none",
          lg: "block",
          xl: "block",
          "@media (max-width:1170px)": "none",
        },
        position: "absolute",
        top: { lg: -320, xl: -320, sm: -220 },
        right: { lg: -320, xl: -320, sm: -220 },
        width: { lg: 920, xl: 920, sm: 600 },
        height: { lg: 920, xl: 920, sm: 600 },
        borderRadius: 32,
        background: t.palette.primary.dark,
        transform: "rotate(12deg)",
        zIndex: 0,
        pointerEvents: "none",
        boxShadow: `${t.shadows[2]}, inset 0 0 120px 0 rgba(0,0,0,0.3)`,
        transition: "all 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
      })}
    />
    <Box
      sx={(t) => ({
        display: {
          xs: "none",
          sm: "none",
          md: "none",
          lg: "block",
          xl: "block",
          "@media (max-width:1170px)": "none",
        },
        position: "absolute",
        left: { lg: -380, xl: -380, sm: -180 },
        bottom: { lg: -380, xl: -380, sm: -180 },
        width: { lg: 900, xl: 900, sm: 500 },
        height: { lg: 900, xl: 900, sm: 500 },
        borderRadius: 40,
        background: t.palette.primary.dark,
        transform: "rotate(-8deg)",
        zIndex: 0,
        pointerEvents: "none",
        boxShadow: `${t.shadows[2]}, inset 0 0 120px 0 rgba(0,0,0,0.3)`,
        transition: "all 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
      })}
    />
    <Grid
      container
      sx={{ minHeight: "100vh", position: "relative", zIndex: 1 }}
    >
      <Grid
        sx={{
          display: { xs: "none", md: "flex" },
          flexDirection: "column",
          alignItems: "flex-start",
          justifyContent: "flex-start",
          minHeight: "100vh",
          flex: 2,
          px: 6,
          py: "20vh",
        }}
        size={{
          xs: 0,
          md: 5,
        }}
      >
        <Box
          sx={{
            width: "100%",
            textAlign: "left",
            position: "relative",
            zIndex: 1,
          }}
        >
          <OrganizationLogo height={95} />
        </Box>
      </Grid>
      <Grid
        sx={{
          display: "flex",
          flex: 3.5,
          alignItems: "center",
          justifyContent: "center",
        }}
        size={{
          xs: 12,
          md: 7,
        }}
      >
        <Box sx={{ p: 8, position: "relative" }}>
          {/* Secondary logo for small screens */}
          <Box
            sx={{
              display: { xs: "flex", md: "none" },
              justifyContent: "center",
              mb: 4,
            }}
          >
            <OrganizationLogo height={56} />
          </Box>
          {children}
        </Box>
      </Grid>
    </Grid>
  </Box>
);

export default LandingPageWrapper;
